import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Pencil, Trash2, X, Shield, Send, XCircle, PhoneCall } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, ROLE_LABELS } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-[#C41E3A]/15 text-[#C41E3A]',
  operations: 'bg-blue-500/15 text-blue-400',
  accounting: 'bg-green-500/15 text-green-400',
  marketing: 'bg-purple-500/15 text-purple-400',
}

interface AdminUser {
  id: string; email: string; name: string; role: string; active: number
  last_login_at: string | null; created_at: string; created_by_name: string | null
  notification_on_call: number
  notify_phone: string | null
}

interface PendingInvite {
  email: string; role: string; expires_at: string; created_at: string
  status: 'pending' | 'expired'
}

const inviteSchema = z.object({
  email: z.string().email('Invalid email'),
  role: z.enum(['super_admin', 'operations', 'accounting', 'marketing']),
})

const editSchema = z.object({
  name: z.string().min(1, 'Required'),
  role: z.enum(['super_admin', 'operations', 'accounting', 'marketing']),
  active: z.boolean(),
})

type InviteData = z.infer<typeof inviteSchema>
type EditData = z.infer<typeof editSchema>

export default function AdminUsers() {
  const { user: me } = useAuth()
  const [modal, setModal] = useState<AdminUser | null | 'new'>(null)
  const qc = useQueryClient()

  const { data } = useQuery<{ users: AdminUser[]; invites: PendingInvite[] }>({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/users').then(r => r.data),
  })

  const [resending, setResending] = useState<string | null>(null)
  const [resendError, setResendError] = useState<string | null>(null)
  // Two-step delete on invites — first tap arms the row, second tap confirms.
  // Lower bar than a modal but still prevents fat-finger cancels.
  const [armedDelete, setArmedDelete] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState<string | null>(null)

  async function resendInvite(email: string) {
    setResending(email)
    setResendError(null)
    try {
      await api.post('/users/invites/resend', { email })
      qc.invalidateQueries({ queryKey: ['admin-users'] })
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      setResendError(err.response?.data?.message ?? 'Failed to resend invite')
    } finally {
      setResending(null)
    }
  }

  async function cancelInvite(email: string) {
    setCancelling(email)
    setResendError(null)
    try {
      await api.delete(`/users/invites?email=${encodeURIComponent(email)}`)
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      setArmedDelete(null)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      setResendError(err.response?.data?.message ?? 'Failed to cancel invite')
    } finally {
      setCancelling(null)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-semibold text-white">Admin Users</h1>
          <Link
            to="/permissions"
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2.5 py-1.5 rounded-lg border border-[#2e2e2e] hover:border-[#3a3a3a] transition-colors"
          >
            <Shield size={13} /> View permissions matrix →
          </Link>
        </div>
        <button
          onClick={() => setModal('new')}
          className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm px-4 py-2 rounded-lg transition-colors"
        >
          <Plus size={15} /> Invite Admin
        </button>
      </div>

      {resendError && (
        <div className="mb-3 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          {resendError}
        </div>
      )}

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              {['Name', 'Email', 'Role', 'Status', 'On-call', 'Last Login', 'Created By', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.users.map((u) => (
              <tr key={u.id} className={`border-b border-[#222] hover:bg-white/2 transition-colors ${!u.active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3">
                  <p className="text-white font-medium">{u.name}</p>
                  {u.id === me?.id && <span className="text-[10px] text-gray-600">You</span>}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[u.role] ?? ''}`}>
                    {ROLE_LABELS[u.role] ?? u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.active
                    ? <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                    : <span className="text-xs bg-gray-500/15 text-gray-500 px-2 py-0.5 rounded-full">Inactive</span>
                  }
                </td>
                <td className="px-4 py-3">
                  <OnCallToggle user={u} />
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{u.last_login_at ? formatDateTime(u.last_login_at) : '—'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{u.created_by_name ?? '—'}</td>
                <td className="px-4 py-3">
                  {u.id !== me?.id && (
                    <button onClick={() => setModal(u)} className="text-gray-500 hover:text-white transition-colors">
                      <Pencil size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data?.invites.map((inv) => (
              <tr key={`invite-${inv.email}`} className="border-b border-[#222] hover:bg-white/2 transition-colors">
                <td className="px-4 py-3 text-gray-500 italic text-xs">(awaiting acceptance)</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{inv.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[inv.role] ?? ''}`}>
                    {ROLE_LABELS[inv.role] ?? inv.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {inv.status === 'pending'
                    ? <span className="text-xs bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">Invite pending</span>
                    : <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Invite expired</span>
                  }
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">—</td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {inv.status === 'pending'
                    ? <>Expires {formatDateTime(inv.expires_at)}</>
                    : <>Expired {formatDateTime(inv.expires_at)}</>
                  }
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">Invited {formatDateTime(inv.created_at)}</td>
                <td className="px-4 py-3">
                  {armedDelete === inv.email ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => cancelInvite(inv.email)}
                        disabled={cancelling === inv.email}
                        className="flex items-center gap-1 text-xs text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded-md disabled:opacity-50 transition-colors"
                      >
                        <XCircle size={12} /> {cancelling === inv.email ? 'Cancelling…' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setArmedDelete(null)}
                        disabled={cancelling === inv.email}
                        className="text-xs text-gray-500 hover:text-white px-2 py-1 rounded-md hover:bg-white/5 transition-colors"
                      >
                        Keep
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => resendInvite(inv.email)}
                        disabled={resending === inv.email}
                        title="Resend invitation email + extend expiry"
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2 py-1 rounded-md hover:bg-white/5 disabled:opacity-50 transition-colors"
                      >
                        <Send size={12} /> {resending === inv.email ? 'Sending…' : 'Resend'}
                      </button>
                      <button
                        onClick={() => setArmedDelete(inv.email)}
                        title="Cancel this invitation"
                        className="text-gray-500 hover:text-red-400 p-1 rounded-md hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        modal === 'new'
          ? <InviteModal onClose={() => setModal(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setModal(null) }} />
          : <EditModal user={modal} onClose={() => setModal(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setModal(null) }} />
      )}
    </div>
  )
}

function InviteModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<InviteData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { role: 'operations' },
  })
  const [apiError, setApiError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  async function onSubmit(data: InviteData) {
    setApiError(null)
    try {
      await api.post('/auth/invite', data)
      setSent(true)
      setTimeout(onSaved, 1800)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      setApiError(err.response?.data?.message ?? 'Failed to send invitation')
    }
  }

  return (
    <Modal title="Invite Admin User" onClose={onClose}>
      {sent ? (
        <div className="text-center space-y-3 py-2">
          <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p className="text-sm text-white font-medium">Invitation sent!</p>
          <p className="text-xs text-gray-500">They'll receive an email with a link to set up their account.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <p className="text-xs text-gray-500">
            An invitation email will be sent. The recipient sets their own name and password via the link.
          </p>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Email</label>
            <input {...register('email')} type="email" autoFocus className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" placeholder="colleague@yhaddco.com" />
            {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Role</label>
            <select {...register('role')} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]">
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {apiError && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{apiError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
              {isSubmitting ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function EditModal({ user, onClose, onSaved }: { user: AdminUser; onClose: () => void; onSaved: () => void }) {
  const { register, handleSubmit, formState: { isSubmitting } } = useForm<EditData>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: user.name, role: user.role as EditData['role'], active: !!user.active },
  })
  // Two-step delete: tap Delete → button morphs into "Confirm delete" + Cancel.
  // Server still enforces the no-self-delete + last-super-admin guards.
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  async function onSubmit(data: EditData) {
    await api.put(`/users/${user.id}`, { ...data, active: data.active })
    onSaved()
  }

  async function onDelete() {
    setDeleting(true)
    setDeleteError(null)
    try {
      await api.delete(`/users/${user.id}`)
      onSaved()
    } catch (err: unknown) {
      // Server returns `{ code, message }` on 400/404 — prefer that over the
      // axios generic so guards like "last super admin" surface verbatim.
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string }
      const message = axiosErr?.response?.data?.message ?? axiosErr?.message ?? 'Failed to delete admin'
      setDeleteError(message)
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  return (
    <Modal title="Edit Admin User" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Full Name</label>
          <input {...register('name')} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Role</label>
          <select {...register('role')} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]">
            {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input {...register('active')} type="checkbox" className="w-4 h-4 accent-[#C41E3A]" />
          <span className="text-sm text-gray-300">Active</span>
        </label>

        {deleteError && (
          <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {deleteError}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-[#2e2e2e]">
          {/* Delete sits on the left as the destructive action; Save stays right as the affirmative. */}
          {confirmDelete ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="px-3 py-2 text-xs text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs rounded-lg"
              >
                <Trash2 size={13} />
                {deleting ? 'Deleting…' : 'Confirm delete'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
            >
              <Trash2 size={13} /> Delete admin
            </button>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">{title}</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

// One-click toggle for the on-call flag. Super_admin can flip any
// admin's on-call state directly from the list (mutations gated
// server-side to super_admin per `adminUsersRoutes` middleware).
// Shows an amber "on-call" pill when active, muted grey ghost
// button otherwise. Optimistic update via react-query so the click
// feels instant even on a slow network.
function OnCallToggle({ user }: { user: AdminUser }) {
  const qc = useQueryClient()
  const isOn = user.notification_on_call === 1

  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      api.put(`/users/${user.id}`, { notification_on_call: next }),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ['admin-users'] })
      const prev = qc.getQueryData<{ users: AdminUser[]; invites: unknown[] }>(['admin-users'])
      if (prev) {
        qc.setQueryData(['admin-users'], {
          ...prev,
          users: prev.users.map(u =>
            u.id === user.id ? { ...u, notification_on_call: next ? 1 : 0 } : u
          ),
        })
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['admin-users'], ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })

  const disabled = mutation.isPending

  if (isOn) {
    return (
      <button
        disabled={disabled}
        onClick={() => mutation.mutate(false)}
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/40 hover:bg-amber-500/25 transition-colors px-2 py-0.5 text-xs font-semibold disabled:opacity-60"
        title="Click to remove on-call — critical alerts will stop pinging this admin."
      >
        <PhoneCall size={11} /> On-call
      </button>
    )
  }
  return (
    <button
      disabled={disabled}
      onClick={() => mutation.mutate(true)}
      className="inline-flex items-center gap-1.5 rounded-full bg-transparent text-gray-500 border border-[#2e2e2e] hover:border-amber-500/40 hover:text-amber-400 transition-colors px-2 py-0.5 text-xs font-medium disabled:opacity-60"
      title="Click to set on-call — this admin will receive forced email + SMS on POS SLA breach + payment failure."
    >
      Off
    </button>
  )
}

